import React from 'react'
import { CheckCircle, AlertTriangle, ArrowLeft, Monitor, Plus } from 'lucide-react'
import { cn } from '@/utils/cn'
import { RoomSection, Session } from '@/types'
import { SectionWithImages, SectionStatus } from '@/utils/sectionStatus'
import MobileOverviewCard from './MobileOverviewCard'

interface MobileSessionOverviewProps {
  session: Session
  sections: RoomSection[]
  sectionsWithStatus: SectionWithImages[]
  isSessionReady: boolean
  onSectionTap: (sectionIndex: number) => void
  onComplete: () => void
  onBack: () => void
  onStartNewSession?: () => void
}

const MobileSessionOverview: React.FC<MobileSessionOverviewProps> = ({
  session,
  sections,
  sectionsWithStatus,
  isSessionReady,
  onSectionTap,
  onComplete,
  onBack,
  onStartNewSession,
}) => {
  // Count sections by status
  const statusCounts = sectionsWithStatus.reduce(
    (acc, s) => {
      acc[s.status] = (acc[s.status] || 0) + 1
      return acc
    },
    {} as Record<SectionStatus, number>
  )

  const completeCount = statusCounts.complete || 0
  const issueCount = (statusCounts.mismatch || 0) + (statusCounts.empty || 0)
  const totalCount = sections.length

  // Get section data
  const getSectionData = (section: RoomSection) => {
    const data = sectionsWithStatus.find((s) => s.section.id === section.id)
    return {
      status: data?.status || 'empty',
      imageCount: data?.images.length || 0,
      detectionCount: data?.images.reduce(
        (sum, img) => sum + (img.detections?.length || 0),
        0
      ) || 0,
    }
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-4 safe-area-top">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 -ml-2 text-gray-600 active:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Session Summary</h1>
            <p className="text-sm text-gray-500">{session.name || 'Inventory Session'}</p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-white border-b px-4 py-4">
        <div className="flex items-center justify-around">
          <div className="text-center">
            <div className="text-2xl font-bold text-emerald-600">{completeCount}</div>
            <div className="text-xs text-gray-500">Complete</div>
          </div>
          <div className="w-px h-8 bg-gray-200" />
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-600">{issueCount}</div>
            <div className="text-xs text-gray-500">Issues</div>
          </div>
          <div className="w-px h-8 bg-gray-200" />
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600">{totalCount}</div>
            <div className="text-xs text-gray-500">Total</div>
          </div>
        </div>
      </div>

      {/* Status Message */}
      {isSessionReady ? (
        <div className="mx-4 mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-emerald-800">All sections validated!</p>
            <p className="text-sm text-emerald-700 mt-0.5">
              You can now complete this session.
            </p>
          </div>
        </div>
      ) : (
        <div className="mx-4 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">
              {issueCount} section{issueCount !== 1 ? 's' : ''} need attention
            </p>
            <p className="text-sm text-amber-700 mt-0.5">
              You can correct detected items below, or use the desktop version to add missing detections.
            </p>
          </div>
        </div>
      )}

      {/* Section List */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {sections.map((section, index) => {
          const { status, imageCount, detectionCount } = getSectionData(section)

          return (
            <MobileOverviewCard
              key={section.id}
              section={section}
              status={status}
              imageCount={imageCount}
              detectionCount={detectionCount}
              onTap={() => onSectionTap(index)}
            />
          )
        })}
      </div>

      {/* Footer Actions */}
      <div className="bg-white border-t px-4 py-4 safe-area-bottom">
        {isSessionReady ? (
          <div className="space-y-3">
            <button
              onClick={onComplete}
              className={cn(
                'w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold',
                'bg-emerald-600 text-white',
                'active:bg-emerald-700 transition-colors'
              )}
            >
              <CheckCircle className="h-5 w-5" />
              Complete Session
            </button>
            {onStartNewSession && (
              <button
                onClick={onStartNewSession}
                className={cn(
                  'w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium',
                  'bg-white border-2 border-gray-300 text-gray-700',
                  'active:bg-gray-50 transition-colors'
                )}
              >
                <Plus className="h-5 w-5" />
                Start Another Session
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <button
              onClick={onBack}
              className={cn(
                'w-full flex items-center justify-center gap-2 py-4 rounded-xl font-medium',
                'bg-primary-600 text-white',
                'active:bg-primary-700 transition-colors'
              )}
            >
              Continue Editing
            </button>
            {onStartNewSession && (
              <button
                onClick={onStartNewSession}
                className={cn(
                  'w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium',
                  'bg-white border-2 border-gray-300 text-gray-700',
                  'active:bg-gray-50 transition-colors'
                )}
              >
                <Plus className="h-5 w-5" />
                Start Another Session
              </button>
            )}
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Monitor className="h-4 w-4" />
              <span>Use desktop to add missing items</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default MobileSessionOverview
